import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import {
  Spin,
  Button,
  Tag,
  Typography,
  Alert,
  Select,
  Space,
  Steps,
  Card,
  Progress,
  Input,
} from 'antd';
import {
  CompassOutlined,
  FolderOpenOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
  UserOutlined,
  ArrowRightOutlined,
  SwapOutlined,
  LoginOutlined,
  PlusOutlined,
  ExclamationCircleOutlined,
  SearchOutlined,
  CrownOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { useAtomValue } from 'jotai';
import { ReactComponent as GeocloudLogo } from '~/assets/svg/geocloud_logo.svg';
import { remote_getMyProjects, remote_getProjects } from '~/common/typescript/apis/ProjectAPIs';
import { userACRoles } from '~/stores/UserStore';
import { AC_TLGeo_Role } from '~/common/typescript/services/access_control/AC_Tlgeo';
import { Project_Base } from '~/common/typescript/models/Project';
import { MapData_Base } from '~/common/typescript/models/MapData';
import { getAxiosInstance } from '~/common/react/utils/networkUtil';
import { API_URL_ADMIN } from '~/config/config';
import Routes from '~/routes/routes';

const { Text, Title, Paragraph } = Typography;

export const ActionSplashScreen: React.FC = () => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Resolved parameters
  const rawMapDataId = params.mapDataId || searchParams.get('map_data_id');
  const mapDataId = rawMapDataId ? Number(rawMapDataId) : null;
  const action = searchParams.get('action') || (mapDataId ? 'add_map_data' : null);
  const preferredProjectId = searchParams.get('project_id')
    ? Number(searchParams.get('project_id'))
    : null;

  // States
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [stepPercent, setStepPercent] = useState<number>(20);
  const [statusMessage, setStatusMessage] = useState<string>('Đang khởi tạo phiên làm việc...');
  
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(true);
  const [mapData, setMapData] = useState<MapData_Base | null>(null);
  const [mapDataError, setMapDataError] = useState<string | null>(null);
  const [detectedLocal, setDetectedLocal] = useState<boolean>(false);
  const [detectedProd, setDetectedProd] = useState<boolean>(false);

  const roles = useAtomValue(userACRoles);
  const isAdmin = roles.includes(AC_TLGeo_Role.TLGEO_ADMIN);

  const [projects, setProjects] = useState<Project_Base[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(preferredProjectId);
  const [countdown, setCountdown] = useState<number>(3);
  const [autoNavigate, setAutoNavigate] = useState<boolean>(true);

  // Admin Project Search States
  const [phoneSearchQuery, setPhoneSearchQuery] = useState<string>('');
  const [isSearchingProjects, setIsSearchingProjects] = useState<boolean>(false);
  const [searchFeedback, setSearchFeedback] = useState<{
    type: 'success' | 'warning' | 'error';
    message: string;
  } | null>(null);

  const currentApiBaseUrl = useMemo(() => {
    return (
      (typeof window !== 'undefined' && localStorage.getItem('API_URL_ADMIN')) ||
      API_URL_ADMIN
    );
  }, []);

  // Step 1: Check Auth Session
  useEffect(() => {
    const token = localStorage.getItem('jwt');
    if (!token) {
      setIsLoggedIn(false);
      setStatusMessage('Yêu cầu đăng nhập tài khoản GeoCloud');
      setCurrentStep(0);
      setStepPercent(25);
    } else {
      setIsLoggedIn(true);
      setCurrentStep(1);
      setStepPercent(50);
      setStatusMessage('Đang xác thực thông tin lớp dữ liệu bản đồ...');
    }
  }, []);

  // Step 2: Validate Map Data Info (if mapDataId exists)
  useEffect(() => {
    if (!isLoggedIn) return;

    if (!mapDataId || isNaN(mapDataId)) {
      // No map data specified, proceed directly to project resolution
      setCurrentStep(2);
      setStepPercent(75);
      setStatusMessage('Đang tải danh sách dự án...');
      return;
    }

    let isMounted = true;
    const fetchMapData = async () => {
      try {
        const res = await getAxiosInstance({ hideSystemError: true }).get(
          `/api/map-data-ext/get-one/${mapDataId}`,
          { hideGlobalError: true } as any
        );

        if (!isMounted) return;
        if (res.data) {
          setMapData(res.data);
          setCurrentStep(2);
          setStepPercent(75);
          setStatusMessage(`Đã xác thực lớp "${res.data.name}". Đang kết nối dự án...`);
        }
      } catch (err: any) {
        if (!isMounted) return;
        console.warn('Map data lookup on splash:', err);
        setMapDataError(
          err?.response?.data?.error?.i18n?.vi ||
            'Không tìm thấy dữ liệu bản đồ trên máy chủ hiện tại'
        );

        // Probe alternate environment
        const isLocal =
          currentApiBaseUrl.includes('localhost') ||
          currentApiBaseUrl.includes('127.0.0.1');

        if (!isLocal) {
          try {
            const probe = await axios.get(
              `http://localhost:11000/api/map-data-ext/get-one/${mapDataId}`,
              { timeout: 1500 }
            );
            if (probe.status === 200 && probe.data?.id) {
              setDetectedLocal(true);
            }
          } catch {
            // not found
          }
        } else {
          try {
            const probe = await axios.get(
              `https://strapi.admin.tlgeo.xyz/api/map-data-ext/get-one/${mapDataId}`,
              { timeout: 2000 }
            );
            if (probe.status === 200 && probe.data?.id) {
              setDetectedProd(true);
            }
          } catch {
            // not found
          }
        }

        // Even with error, still allow resolving project
        setCurrentStep(2);
        setStepPercent(75);
      }
    };

    fetchMapData();
    return () => {
      isMounted = false;
    };
  }, [isLoggedIn, mapDataId, currentApiBaseUrl]);

  // Step 3: Fetch User Projects
  useEffect(() => {
    if (!isLoggedIn) return;

    let isMounted = true;
    const fetchProjects = async () => {
      try {
        let list = await remote_getMyProjects();
        if (!isMounted) return;

        // If user is admin and has no personal projects, fallback to system projects
        if ((!list || list.length === 0) && isAdmin) {
          try {
            const res = await remote_getProjects({ page: 1, pageSize: 20 });
            if (!isMounted) return;
            list = res?.results || [];
          } catch (admErr) {
            console.warn('Admin fallback getProjects error:', admErr);
          }
        }

        setProjects(list || []);
        if (list && list.length > 0) {
          if (!selectedProjectId) {
            setSelectedProjectId(list[0].id);
          }
          setCurrentStep(3);
          setStepPercent(100);
          setStatusMessage('Sẵn sàng chuyển tiếp vào dự án bản đồ');
        } else {
          setCurrentStep(3);
          setStepPercent(100);
          setStatusMessage(
            isAdmin
              ? 'Tìm kiếm dự án muốn chọn theo số điện thoại bên dưới'
              : 'Chưa có dự án nào trong tài khoản'
          );
        }
      } catch (err) {
        console.error('Failed to fetch projects in splash:', err);
        if (isMounted) {
          setCurrentStep(3);
          setStepPercent(100);
          setStatusMessage('Không thể tải danh sách dự án');
        }
      }
    };

    if (currentStep >= 2) {
      fetchProjects();
    }

    return () => {
      isMounted = false;
    };
  }, [isLoggedIn, currentStep, isAdmin]);

  // Admin Project Search handler by phone number / project name
  const handleAdminPhoneSearch = async (queryText?: string) => {
    const raw = (queryText !== undefined ? queryText : phoneSearchQuery).trim();
    if (!raw) {
      setIsSearchingProjects(true);
      try {
        let myProjects = await remote_getMyProjects();
        if ((!myProjects || myProjects.length === 0) && isAdmin) {
          const res = await remote_getProjects({ page: 1, pageSize: 20 });
          myProjects = res?.results || [];
        }
        setProjects(myProjects || []);
        if (myProjects && myProjects.length > 0) {
          setSelectedProjectId(myProjects[0].id);
        }
        setSearchFeedback(null);
      } catch (e) {
        console.error('Failed to reset projects:', e);
      } finally {
        setIsSearchingProjects(false);
      }
      return;
    }

    setIsSearchingProjects(true);
    setAutoNavigate(false); // Pause auto-countdown so admin has time to review and choose
    setSearchFeedback(null);

    try {
      // 1. Primary search by phone_number
      let response = await remote_getProjects({
        page: 1,
        pageSize: 50,
        phone_number: raw,
      });

      // 2. If no results found and query has letters or characters, fallback to project_name
      if ((!response?.results || response.results.length === 0) && !/^\d+$/.test(raw)) {
        response = await remote_getProjects({
          page: 1,
          pageSize: 50,
          project_name: raw,
        });
      }

      const results = response?.results || [];
      if (results.length > 0) {
        setProjects(results);
        setSelectedProjectId(results[0].id);
        setSearchFeedback({
          type: 'success',
          message: `Tìm thấy ${response.total || results.length} dự án cho "${raw}"`,
        });
      } else {
        setSearchFeedback({
          type: 'warning',
          message: `Không tìm thấy dự án nào với SĐT / Tên "${raw}"`,
        });
      }
    } catch (err: any) {
      console.error('Failed to search projects for admin:', err);
      setSearchFeedback({
        type: 'error',
        message: 'Lỗi tìm kiếm dự án từ máy chủ',
      });
    } finally {
      setIsSearchingProjects(false);
    }
  };

  // Step 4: Handle countdown and final redirection
  const handleProceed = useCallback(
    (targetProjectId?: number) => {
      const pId = targetProjectId || selectedProjectId;
      if (pId) {
        let targetUrl = `/project/${pId}`;
        if (action && mapDataId) {
          targetUrl += `?action=${action}&map_data_id=${mapDataId}`;
        }
        navigate(targetUrl, { replace: true });
      } else {
        navigate(Routes.PROJECT, { replace: true });
      }
    },
    [selectedProjectId, action, mapDataId, navigate]
  );

  useEffect(() => {
    if (currentStep === 3 && selectedProjectId && autoNavigate && !mapDataError) {
      if (countdown > 0) {
        const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
        return () => clearTimeout(timer);
      } else {
        handleProceed();
      }
    }
  }, [currentStep, selectedProjectId, autoNavigate, countdown, mapDataError, handleProceed]);

  const handleSwitchServer = (targetUrl: string) => {
    localStorage.setItem('API_URL_ADMIN', targetUrl);
    window.location.reload();
  };

  return (
    <SplashContainer>
      <GlowBackground />
      <SplashCard>
        {/* Brand Header */}
        <LogoWrapper>
          <GeocloudLogo height={44} />
          <BrandSubtitle>Nền tảng GIS & Quản trị dữ liệu không gian trực tuyến</BrandSubtitle>
        </LogoWrapper>

        {/* Step progress bar */}
        <div style={{ margin: '24px 0 16px 0' }}>
          <Progress
            percent={stepPercent}
            status={mapDataError ? 'exception' : currentStep === 3 ? 'success' : 'active'}
            showInfo={false}
            strokeColor={{
              '0%': '#38bdf8',
              '100%': '#2563eb',
            }}
          />
          <StatusRow>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 16, color: '#0284c7' }} spin />} />
            <Text style={{ fontSize: 13, color: '#334155', fontWeight: 500 }}>
              {statusMessage}
            </Text>
          </StatusRow>
        </div>

        {/* Not Logged In State */}
        {!isLoggedIn && (
          <Alert
            type="warning"
            showIcon
            icon={<UserOutlined />}
            message="Yêu cầu đăng nhập tài khoản GeoCloud"
            description="Bạn cần đăng nhập để truy cập vào các dự án không gian và xem dữ liệu bản đồ được phân quyền."
            action={
              <Button
                type="primary"
                icon={<LoginOutlined />}
                onClick={() => {
                  navigate(
                    `/login?goto=${encodeURIComponent(
                      window.location.pathname + window.location.search
                    )}`
                  );
                }}
              >
                Đăng nhập ngay
              </Button>
            }
          />
        )}

        {/* Server Mismatch Alerts */}
        {isLoggedIn && detectedLocal && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="Phát hiện dữ liệu trên máy chủ Local"
            description={
              <div>
                Lớp dữ liệu <b>#{mapDataId}</b> tồn tại trên Strapi Local (<code>http://localhost:11000</code>
                ), trong khi GeoCloud đang trỏ tới <code>{currentApiBaseUrl}</code>.
              </div>
            }
            action={
              <Button
                type="primary"
                size="small"
                icon={<SwapOutlined />}
                onClick={() => handleSwitchServer('http://localhost:11000')}
              >
                Đổi sang Local
              </Button>
            }
          />
        )}

        {isLoggedIn && detectedProd && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="Phát hiện dữ liệu trên máy chủ Production"
            description={
              <div>
                Lớp dữ liệu <b>#{mapDataId}</b> tồn tại trên máy chủ Production (
                <code>https://strapi.admin.tlgeo.xyz</code>).
              </div>
            }
            action={
              <Button
                type="primary"
                size="small"
                icon={<SwapOutlined />}
                onClick={() => handleSwitchServer('https://strapi.admin.tlgeo.xyz')}
              >
                Đổi sang Production
              </Button>
            }
          />
        )}

        {/* MapData Preview Card if resolved */}
        {isLoggedIn && mapData && (
          <DataPreviewCard>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <FolderOpenOutlined style={{ fontSize: 24, color: '#0284c7' }} />
              <div style={{ flex: 1 }}>
                <Text strong style={{ fontSize: 14, color: '#0f172a' }}>
                  {mapData.name}
                </Text>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Mã #{mapData.id} • {mapData.category || 'USER_DATA'}
                  </Text>
                </div>
              </div>
              <Tag color="cyan">
                {mapData.map_data_files?.[0]?.type?.toUpperCase() || 'GIS LAYER'}
              </Tag>
            </div>
          </DataPreviewCard>
        )}

        {/* Project Selection / Confirmation */}
        {isLoggedIn && currentStep === 3 && (
          <div style={{ marginTop: 16 }}>
            {isAdmin && (
              <AdminSection>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 8,
                  }}
                >
                  <Space size={6}>
                    <CrownOutlined style={{ color: '#eab308', fontSize: 16 }} />
                    <Text strong style={{ fontSize: 13, color: '#1e293b' }}>
                      Đặc quyền Admin: Tìm & Chọn dự án bất kỳ
                    </Text>
                  </Space>
                  <Tag color="gold" style={{ margin: 0, fontWeight: 600 }}>
                    ADMIN
                  </Tag>
                </div>
                <Input.Search
                  placeholder="Nhập số điện thoại chủ sở hữu (hoặc tên dự án)..."
                  allowClear
                  enterButton={
                    <Button type="primary" icon={<SearchOutlined />}>
                      Tìm kiếm
                    </Button>
                  }
                  loading={isSearchingProjects}
                  value={phoneSearchQuery}
                  onChange={(e) => {
                    setPhoneSearchQuery(e.target.value);
                    setAutoNavigate(false);
                  }}
                  onSearch={(val) => handleAdminPhoneSearch(val)}
                  style={{ marginBottom: searchFeedback ? 6 : 8 }}
                />
                {searchFeedback && (
                  <Alert
                    type={searchFeedback.type}
                    message={searchFeedback.message}
                    showIcon
                    style={{ marginBottom: 4, padding: '4px 10px', fontSize: 12 }}
                  />
                )}
              </AdminSection>
            )}

            {projects.length > 0 ? (
              <div>
                <div
                  style={{
                    marginBottom: 8,
                    fontSize: 13,
                    color: '#475569',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <b>Dự án đích mở bản đồ:</b>
                  {isAdmin && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {projects.length} dự án khả dụng
                    </Text>
                  )}
                </div>
                <Select
                  style={{ width: '100%', marginBottom: 16 }}
                  value={selectedProjectId}
                  showSearch
                  optionFilterProp="label"
                  onChange={(val) => {
                    setSelectedProjectId(val);
                    setAutoNavigate(false); // Pause auto countdown if manually selected
                  }}
                  options={projects.map((p) => {
                    const ownerText = p.owner
                      ? ` • SĐT: ${p.owner.username || p.owner.phoneNumber || ''}${
                          p.owner.fullname ? ` (${p.owner.fullname})` : ''
                        }`
                      : '';
                    return {
                      value: p.id,
                      label: `${p.name} (ID: ${p.id})${ownerText}`,
                    };
                  })}
                />

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {autoNavigate && !mapDataError
                      ? `Tự động chuyển tiếp sau ${countdown}s...`
                      : 'Đã sẵn sàng mở bản đồ'}
                  </Text>
                  <Button
                    type="primary"
                    icon={<ArrowRightOutlined />}
                    onClick={() => handleProceed()}
                  >
                    Vào dự án ngay
                  </Button>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '12px 0' }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                  {isAdmin
                    ? 'Chưa chọn dự án nào. Vui lòng nhập số điện thoại ở trên để tìm kiếm dự án cần chọn.'
                    : 'Tài khoản của bạn chưa có dự án nào. Bạn có thể mở trang quản lý dự án để khởi tạo.'}
                </Text>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => navigate(Routes.PROJECT)}
                >
                  Tạo hoặc quản lý dự án
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Error State Fallback Actions */}
        {isLoggedIn && mapDataError && !detectedLocal && !detectedProd && (
          <Alert
            type="error"
            showIcon
            icon={<ExclamationCircleOutlined />}
            style={{ marginTop: 16 }}
            message="Không tìm thấy lớp dữ liệu bản đồ"
            description={
              <div>
                <div>{mapDataError}</div>
                <div style={{ marginTop: 8 }}>
                  Bạn vẫn có thể tiếp tục vào màn hình bản đồ để thao tác bình thường.
                </div>
              </div>
            }
            action={
              <Button size="small" onClick={() => handleProceed()}>
                Vào dự án
              </Button>
            }
          />
        )}
      </SplashCard>
    </SplashContainer>
  );
};

export default ActionSplashScreen;

// Animations & Styles
const pulse = keyframes`
  0% {
    transform: scale(0.95);
    opacity: 0.6;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.9;
  }
  100% {
    transform: scale(0.95);
    opacity: 0.6;
  }
`;

const SplashContainer = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(circle at 50% 30%, #f1f5f9 0%, #e2e8f0 100%);
  z-index: 99999;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
`;

const GlowBackground = styled.div`
  position: absolute;
  width: 480px;
  height: 480px;
  background: radial-gradient(circle, rgba(56, 189, 248, 0.22) 0%, rgba(37, 99, 235, 0.05) 70%, transparent 100%);
  border-radius: 50%;
  animation: ${pulse} 5s ease-in-out infinite;
  pointer-events: none;
`;

const SplashCard = styled.div`
  position: relative;
  width: 520px;
  max-width: 92vw;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 16px;
  padding: 32px 36px;
  box-shadow: 0 20px 45px -10px rgba(15, 23, 42, 0.12), 0 0 1px 1px rgba(15, 23, 42, 0.05);
`;

const LogoWrapper = styled.div`
  text-align: center;
  margin-bottom: 8px;
`;

const BrandSubtitle = styled.div`
  font-size: 13px;
  color: #64748b;
  margin-top: 6px;
  font-weight: 500;
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
`;

const DataPreviewCard = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px 14px;
  margin-top: 14px;
`;

const AdminSection = styled.div`
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 14px;
`;

